import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, CheckCircle2, Clock, Save, Send, AlertTriangle, Car, Zap, Phone, Mail, HeadphonesIcon, ShieldCheck, User, Package, QrCode, CalendarDays, MapPin } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatEU, fmtEUR } from "../components/public-book/formatters";
import { COUNTRIES, DIAL_CODES, DEFAULT_DIAL_CODE } from "../components/public-book/geo";
import EUDatePicker from "../components/EUDatePicker";
import { DigitalRentalPassModal } from "../components/DigitalRentalPass";
import DriverVerificationCard from "../components/DriverVerificationCard";
import { useAuth } from "../context/AuthContext";

/**
 * Public Self-Service Portal (`/manage-booking`).
 *
 * Guests locate their reservation with the 8-char booking reference +
 * email. From there they can:
 *   • Free-edit flight / ferry / notes → instant save.
 *   • Request date or extra changes → routed to admin approval queue.
 *
 * Both flows respect the admin-configured `modification_cutoff_hours`
 * window — inside the cutoff the whole panel goes read-only with a
 * "please call our office" notice.
 */
export default function ManageBooking() {
  // v4.2.2 — brand pulled from Business Settings.
  const [brandName, setBrandName] = useState("Brainart Rent");
  const [sp] = useSearchParams();
  const [reference, setReference] = useState(sp.get("ref") || "");
  const [email, setEmail] = useState(sp.get("email") || "");
  const [state, setState] = useState(null); // { booking, locked, lock_reason }
  const [loading, setLoading] = useState(false);
  // v4.0.1 — Dynamic business settings for Reservations Team card + any
  // future checkout-field references. Fetched once on mount.
  const [publicSettings, setPublicSettings] = useState(null);
  useEffect(() => {
    api.get("/settings/public")
      .then((r) => {
        setPublicSettings(r.data);
        if (r.data?.brand_name) setBrandName(r.data.brand_name);
      })
      .catch(() => setPublicSettings(null));
  }, []);

  const lookup = async () => {
    if (!reference.trim() || !email.trim()) {
      toast.warning("Both fields are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/public/manage-booking/lookup", {
        booking_reference: reference.trim(),
        email:             email.trim(),
      });
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      setState(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If ?ref= & ?email= are in the URL, auto-fetch on mount.
    if (sp.get("ref") && sp.get("email")) lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-neutral-900" data-testid="manage-booking-page">
      <header className="border-b border-black/8 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-3.5 flex flex-wrap items-center justify-between gap-y-2">
          <a href="/book" className="font-display text-xl tracking-tight">{brandName}</a>
          <nav className="text-xs text-neutral-500 flex items-center gap-5">
            <a href="/book" className="hover:text-black">Book again</a>
            <a href="/manage-booking" className="hover:text-black" data-testid="mb-nav-manage-booking">Manage Booking</a>
            <a href="mailto:hello@brainart.gr" className="hover:text-black">Contact</a>
          </nav>
        </div>
      </header>

      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-7 sm:pt-10 pb-8">
        {/* v4.0.3 — Explicit back-to-website nav, since /manage-booking
            is a standalone page that customers reach from a direct link
            in their confirmation email. */}
        <a
          href="/book"
          className="inline-flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-full border border-black/20 text-neutral-700 bg-white hover:bg-black hover:text-white hover:border-black transition-colors mb-5"
          data-testid="mb-back-to-website"
        >
          <span aria-hidden="true">←</span> Back to website
        </a>
        <p className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">Customer portal</p>
        <h1 className="font-display text-4xl md:text-5xl leading-[1.05] mt-3">Manage your booking.</h1>
        <p className="text-neutral-500 mt-3 max-w-xl text-sm">
          Update flight or ferry details, tweak dates, or add extras — all in a couple of clicks.
        </p>
      </section>

      {!state && (
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 mb-10">
          <form
            onSubmit={(e) => { e.preventDefault(); lookup(); }}
            className="bg-white border border-black/10 rounded-lg shadow-sm p-4 lg:p-5 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 lg:gap-4"
            data-testid="manage-booking-form"
          >
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Booking reference</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)} className="pb-input" placeholder="e.g. A1B2C3D4" data-testid="mb-ref-input" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pb-input" placeholder="you@example.com" data-testid="mb-email-input" />
            </label>
            <button type="submit" disabled={loading}
              className="bg-black text-white text-sm font-medium px-6 py-3 rounded inline-flex items-center justify-center gap-2 hover:bg-neutral-800 disabled:opacity-50"
              data-testid="mb-lookup-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Find booking
            </button>
          </form>
          <p className="text-xs text-neutral-500 mt-3 max-w-md">
            Your booking reference is the 8-character code shown on the confirmation screen and email.
          </p>
        </section>
      )}

      {state && (
        <BookingPanel state={state} onRefresh={lookup} publicSettings={publicSettings} />
      )}

      <footer className="border-t border-black/8 mt-10 py-8 text-center text-xs text-neutral-500 space-y-1">
        <p>{brandName} · Customer Portal</p>
        <p data-testid="mb-footer-copyright">
          © {new Date().getFullYear()} {brandName}. All rights reserved.
        </p>
      </footer>

      <style>{`
        /* v4.7.4 — one uniform control height across every guest-portal field
           (text, time, select and the date-picker trigger button). */
        .pb-input {
          box-sizing: border-box;
          width: 100%;
          height: 44px;
          line-height: 1.25;
          border: 1px solid rgba(0,0,0,0.15);
          background: #FFF;
          font-size: 14px;
          padding: 10px 12px;
          border-radius: 6px;
          color: #111;
          outline: none;
          transition: border-color .18s ease;
          -webkit-appearance: none;
          appearance: none;
        }
        button.pb-input { display: flex; align-items: center; }
        input[type="time"].pb-input, input[type="date"].pb-input {
          min-height: 44px;
          font-variant-numeric: tabular-nums;
        }
        .pb-input:focus { border-color: #111; }
        .pb-input:disabled { background: #F8FAFC; color: #475569; }
        textarea.pb-input, .pb-input[rows] { height: auto; min-height: 72px; }
      `}</style>
    </div>
  );
}

function TripLeg({ label, date, time, place, testId }) {
  return (
    <div className="rounded-lg bg-neutral-50 border border-black/5 px-3 py-2.5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</p>
      <p className="text-sm text-neutral-900 mt-1 tabular-nums flex items-center gap-1.5 whitespace-nowrap">
        <CalendarDays size={13} className="text-neutral-400 shrink-0" />
        {date}{time ? ` · ${time}` : ""}
      </p>
      <p className="text-sm text-neutral-600 mt-0.5 flex items-start gap-1.5">
        <MapPin size={13} className="text-neutral-400 shrink-0 mt-0.5" />
        <span className="break-words">{place || "—"}</span>
      </p>
    </div>
  );
}

function StaffHandoverBanner({ booking }) {
  const { user } = useAuth() || {};
  const STAFF = ["super_admin", "admin", "editor", "staff"];
  if (!user || !STAFF.includes(user.role)) return null;

  const handedOver = booking.status === "active";
  return (
    <div
      className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3"
      data-testid="mb-staff-banner"
    >
      <div className="flex items-center gap-2.5 text-sm text-sky-900">
        <ShieldCheck size={18} className="text-sky-600" />
        <span>
          <strong>Staff detected</strong> · signed in as {user.email}
          {handedOver && booking.handed_over_by
            ? ` — handed over by ${booking.handed_over_by}`
            : ""}
        </span>
      </div>
      {!handedOver && (
        <a
          href={`/admin/bookings?handover=${booking.id}`}
          className="px-3.5 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors"
          data-testid="mb-staff-handover-link"
        >
          <Zap size={13} /> Express Handover Desk Action
        </a>
      )}
    </div>
  );
}

function BookingPanel({ state, onRefresh, publicSettings }) {
  const { booking, locked, lock_reason } = state;
  const shortRef = (booking.id || "").slice(0, 8).toUpperCase();

  const [flight, setFlight] = useState({
    airline_company: booking.airline_company || "",
    flight_number:   booking.flight_number   || "",
    flight_time:     booking.flight_time     || "",
  });
  const [ferry, setFerry] = useState({
    company:      booking.ferry_details?.company || "",
    vessel_name:  booking.ferry_details?.vessel_name || "",
    arrival_time: booking.ferry_details?.arrival_time || "",
  });
  const [notes, setNotes] = useState(booking.notes || "");
  // v3.9.9 — customer identity (email intentionally locked).
  // v4.0.0 — Phone parsed into dial code + local digits. Best-effort
  // split: if the stored value starts with a `+` we take the leading
  // token as the dial code, otherwise default to +30.
  const parsePhone = (raw) => {
    const val = (raw || "").trim();
    const match = val.match(/^(\+\d{1,4})\s*(.*)$/);
    if (match) return { code: match[1], local: match[2] };
    return { code: DEFAULT_DIAL_CODE, local: val };
  };
  const _initialPhone = parsePhone(booking.phone);
  const [phoneCode, setPhoneCode]   = useState(_initialPhone.code);
  const [phoneLocal, setPhoneLocal] = useState(_initialPhone.local);
  const [profile, setProfile] = useState({
    full_name:      booking.full_name      || "",
    id_passport:    booking.id_passport    || "",
    license_number: booking.license_number || "",
    driver_age:     booking.driver_age     ?? "",
    country:        booking.country        || "",
    address:        booking.address        || "",
    city:           booking.city           || "",
    zip:            booking.zip            || "",
  });
  const [busy, setBusy] = useState(false);
  // v4.7.3 — Express Check-In window: "open" from confirmation up to the
  // configurable cutoff before pickup, "closed" inside that cutoff, "hidden"
  // once the rental is over (past dropoff) or the booking is completed /
  // cancelled / in rent. v4.7.8 — cutoff is now admin-configurable
  // (`express_checkin_cutoff_hours`, default 1h) instead of hardcoded.
  const checkinCutoffHours = Number(publicSettings?.express_checkin_cutoff_hours ?? 1);
  const checkInWindow = (() => {
    const toDate = (d, t) => (d ? new Date(`${d}T${(t || "00:00").slice(0, 5)}:00`) : null);
    const pickup = toDate(booking.pickup_date, booking.pickup_time);
    const dropoff = toDate(booking.return_date, booking.return_time || "23:59");
    const now = new Date();
    if (["completed", "cancelled", "declined", "active"].includes(booking.status)) return "hidden";
    if (dropoff && now > dropoff) return "hidden";
    if (booking.status !== "confirmed" || !pickup) return "hidden";
    return now <= new Date(pickup.getTime() - checkinCutoffHours * 60 * 60 * 1000) ? "open" : "closed";
  })();
  // v4.0.5 — Digital Rental Pass modal (QR deep-link + print).
  const [showPass, setShowPass] = useState(false);
  // v4.1.0 — Digital Driver Verification state (documents + e-signature).
  const [verification, setVerification] = useState(booking.driver_verification || null);
  // v4.0.3 — Post-save subtle link ("Return to homepage") shown briefly
  // after a successful contact/quick save. Auto-dismisses on next edit.
  const [justSaved, setJustSaved] = useState(false);
  // v4.0.0 — Extras & Add-ons catalogue (fetched once, pre-checked from booking.addons).
  const [availableExtras, setAvailableExtras]   = useState([]);
  const [selectedExtras, setSelectedExtras]     = useState(
    () => (booking.addons || []).reduce((acc, id) => ({ ...acc, [id]: true }), {}),
  );
  useEffect(() => {
    api.get("/extras?status=active")
      .then((r) => setAvailableExtras(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAvailableExtras([]));
  }, []);

  const [dates, setDates] = useState({
    pickup_date: booking.pickup_date, pickup_time: booking.pickup_time,
    return_date: booking.return_date, return_time: booking.return_time,
  });
  const [vehicleId, setVehicleId] = useState(booking.vehicle_id || "");
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const majorDirty = useMemo(() =>
    dates.pickup_date !== booking.pickup_date
    || dates.return_date !== booking.return_date
    || dates.pickup_time !== booking.pickup_time
    || dates.return_time !== booking.return_time
    || vehicleId !== (booking.vehicle_id || ""),
    [dates, vehicleId, booking],
  );

  useEffect(() => {
    if (!dates.pickup_date || !dates.return_date) return;
    setLoadingVehicles(true);
    api.get("/public/vehicles", {
      params: {
        pickup_date:     dates.pickup_date,
        return_date:     dates.return_date,
        pickup_time:     dates.pickup_time,
        return_time:     dates.return_time,
        pickup_location: booking.pickup_location,
      },
    })
      .then((r) => setAvailableVehicles((r.data?.vehicles || []).filter((v) => v.available_for_dates !== false)))
      .catch(() => setAvailableVehicles([]))
      .finally(() => setLoadingVehicles(false));
  }, [dates.pickup_date, dates.return_date, dates.pickup_time, dates.return_time, booking.pickup_location]);

  const currentVehicleAvailable = useMemo(() => {
    if (!availableVehicles.length) return true;
    if (!majorDirty) return true;
    return availableVehicles.some((v) => v.id === (booking.vehicle_id || ""));
  }, [availableVehicles, booking.vehicle_id, majorDirty]);

  const saveQuick = async () => {
    if (locked) { toast.error(lock_reason); return; }
    setBusy(true);
    try {
      await api.patch(`/public/manage-booking/${booking.id}/quick-edit`, {
        email: booking.user_email,
        ...flight,
        ferry_company: ferry.company,
        ferry_vessel:  ferry.vessel_name,
        ferry_arrival: ferry.arrival_time,
        notes,
        // v3.9.9 — profile edits · v4.0.0 phone re-assembled from split
        full_name:      profile.full_name,
        phone:          phoneLocal.trim() ? `${phoneCode} ${phoneLocal.trim()}` : "",
        id_passport:    profile.id_passport,
        license_number: profile.license_number,
        driver_age:     profile.driver_age === "" ? null : Number(profile.driver_age),
        country:        profile.country,
        address:        profile.address,
        city:           profile.city,
        zip:            profile.zip,
      });
      toast.success("Details saved");
      setJustSaved(true);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const requestChange = async () => {
    if (locked) { toast.error(lock_reason); return; }
    if (!majorDirty) { toast.warning("No changes to submit."); return; }
    setBusy(true);
    try {
      await api.post(`/public/manage-booking/${booking.id}/request-modification`, {
        email: booking.user_email,
        ...dates,
        vehicle_id: vehicleId || undefined,
      });
      toast.success("Modification request submitted — the office will confirm shortly.");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-5 sm:pt-6 pb-14" data-testid="mb-booking-panel">
      {showPass && (
        <DigitalRentalPassModal
          booking={{ ...booking, booking_reference: shortRef, customer_email: booking.user_email, driver_verification: verification }}
          companyName={publicSettings?.company_name}
          onClose={() => setShowPass(false)}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          <StaffHandoverBanner booking={booking} />
          {/* ── SUMMARY CARD — mobile-first stacked layout (v4.7.2) ──── */}
          <div className="bg-white border border-black/10 rounded-xl p-5 sm:p-7 shadow-sm" data-testid="mb-summary">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-5">
              <div className="min-w-0 flex-1">
                <p className="text-xs tracking-[0.25em] uppercase text-neutral-500">Reservation</p>
                <p className="font-mono text-xl mt-2 text-neutral-900" data-testid="mb-reference">{shortRef}</p>
                <p className="font-display text-2xl sm:text-3xl mt-3 text-neutral-900 break-words">
                  {booking.vehicle_snapshot?.name || booking.vehicle_snapshot?.brand || "Vehicle"}
                </p>
                <span className="inline-block mt-3 text-[11px] font-medium uppercase tracking-widest px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700" data-testid="mb-duration-pill">
                  {booking.days} day{booking.days === 1 ? "" : "s"}
                </span>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <TripLeg
                    label="Pickup"
                    date={formatEU(booking.pickup_date)}
                    time={booking.pickup_time}
                    place={booking.pickup_location}
                    testId="mb-leg-pickup"
                  />
                  <TripLeg
                    label="Dropoff"
                    date={formatEU(booking.return_date)}
                    time={booking.return_time}
                    place={booking.return_location}
                    testId="mb-leg-dropoff"
                  />
                </div>
              </div>

              <div className="flex items-end justify-between gap-4 sm:block sm:text-right shrink-0">
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-neutral-500">Total</p>
                  <p className="font-display text-3xl sm:text-4xl tabular-nums text-neutral-900" data-testid="mb-total">{fmtEUR(booking.total || 0)}</p>
                </div>
                <p className={`inline-block text-xs uppercase tracking-widest px-2.5 py-1 rounded font-medium sm:mt-2 ${
                  booking.status === "confirmed" ? "bg-emerald-100 text-emerald-800"
                  : booking.status === "active" ? "bg-blue-100 text-blue-800"
                  : booking.status === "modification_requested" ? "bg-amber-100 text-amber-800"
                  : booking.status === "cancelled" ? "bg-rose-100 text-rose-800"
                  : "bg-neutral-200 text-neutral-700"
                }`} data-testid="mb-status-pill">{booking.status?.replace(/_/g, " ")}</p>
              </div>
            </div>

            {/* Actions — full width on mobile, right-aligned from sm up */}
            <div className="mt-5 pt-4 border-t border-black/5 grid grid-cols-1 sm:flex sm:justify-end gap-2.5" data-testid="mb-summary-actions">
              <button
                type="button"
                onClick={() => setShowPass(true)}
                className="w-full sm:w-auto justify-center px-3.5 py-2.5 sm:py-1.5 bg-neutral-900 hover:bg-neutral-800 text-white text-xs font-semibold rounded-lg shadow-sm inline-flex items-center gap-1.5 transition-colors"
                data-testid="mb-show-pass-btn"
              >
                <QrCode size={13} /> Show Digital Key Pass
              </button>
              {checkInWindow === "open" && (
                <button
                  type="button"
                  onClick={() => {
                    toast.success("Complete your driver details & signature below for an express desk handover.");
                    document.getElementById("mb-driver-verification")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="w-full sm:w-auto justify-center px-3.5 py-2.5 sm:py-1.5 bg-sky-100 hover:bg-sky-200 text-sky-800 text-xs font-semibold rounded-lg shadow-sm inline-flex items-center gap-1.5 transition-colors"
                  data-testid="mb-express-checkin-btn"
                >
                  <Zap size={13} className="text-sky-600" /> Express Check-In
                </button>
              )}
            </div>

            {checkInWindow === "closed" && (
              <div
                className="mt-4 flex gap-2.5 p-3.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm"
                data-testid="mb-express-checkin-closed"
              >
                <Clock size={16} className="shrink-0 mt-0.5 text-amber-600" />
                <p>
                  Online Check-In is closed for this departure (cutoff is {checkinCutoffHours}h prior).
                  Please complete check-in at the desk.
                </p>
              </div>
            )}
          </div>

          {locked && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-base text-rose-800 flex items-start gap-3" data-testid="mb-locked-notice">
              <Lock size={20} className="mt-0.5" />
              <div>
                <p className="font-semibold">{lock_reason}</p>
                <p className="text-sm mt-1 text-rose-700">Our Reservations Team can still update your booking by phone.</p>
              </div>
            </div>
          )}

          {booking.pending_modification && (
            <PendingModificationCard pending={booking.pending_modification} bookingTotal={booking.total} />
          )}

          {/* ── DIGITAL DRIVER VERIFICATION (v4.1.0) ─────────────────── */}
          <div id="mb-driver-verification" className="scroll-mt-20">
            <DriverVerificationCard
              bookingId={booking.id}
              verification={verification}
              onUpdate={setVerification}
            />
          </div>

          {/* ── CUSTOMER PROFILE (v3.9.9 · v4.0.0 header + phone split) ── */}
          <div className="bg-white border border-black/10 rounded-xl p-5 sm:p-7 shadow-sm" data-testid="mb-profile-card">
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <h2 className="font-display text-2xl inline-flex items-center gap-2">
                <User size={18} /> Driver &amp; Contact Details
              </h2>
              <span className="text-xs uppercase tracking-widest text-neutral-500">Instant save</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase px-2.5 py-1 rounded bg-neutral-900 text-white" data-testid="mb-reservation-badge">
                <Lock size={11} /> Reservation {shortRef}
              </span>
              <p className="text-xs text-neutral-500">
                Email address and reservation reference cannot be changed online.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Full name">
                <input disabled={locked} value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="pb-input" data-testid="mb-full-name" />
              </Field>
              <Field label="Email (locked)">
                <input
                  type="email"
                  value={booking.user_email || ""}
                  disabled
                  readOnly
                  style={{ color: "#9ca3af", backgroundColor: "#f5f5f5" }}
                  className="pb-input cursor-not-allowed select-none border-neutral-200"
                  data-testid="mb-email-locked"
                />
              </Field>
              <Field label="Mobile phone">
                <div
                  className="flex items-stretch rounded-lg border border-neutral-300 overflow-hidden focus-within:ring-2 focus-within:ring-neutral-900"
                  data-testid="mb-phone-group"
                >
                  <select
                    disabled={locked}
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value)}
                    className="w-24 shrink-0 bg-neutral-50 border-r border-neutral-300 px-2 text-sm focus:outline-none tabular-nums"
                    aria-label="Country calling code"
                    data-testid="mb-phone-code"
                  >
                    {DIAL_CODES.map((c) => (
                      <option key={c.iso} value={c.code}>{c.code} ({c.iso})</option>
                    ))}
                  </select>
                  <input
                    disabled={locked}
                    type="tel"
                    inputMode="numeric"
                    value={phoneLocal}
                    onChange={(e) => setPhoneLocal(e.target.value)}
                    className="flex-1 min-w-0 border-0 px-3 py-2 text-sm focus:outline-none bg-white"
                    placeholder="e.g. 6912345678"
                    data-testid="mb-phone"
                  />
                </div>
              </Field>
              <Field label="ID / Passport">
                <input disabled={locked} value={profile.id_passport} onChange={(e) => setProfile({ ...profile, id_passport: e.target.value })} className="pb-input" data-testid="mb-id-passport" />
              </Field>
              <Field label="Driver's license #">
                <input disabled={locked} value={profile.license_number} onChange={(e) => setProfile({ ...profile, license_number: e.target.value })} className="pb-input" data-testid="mb-license" />
              </Field>
              <Field label="Driver age">
                <input disabled={locked} type="number" min="17" max="99" value={profile.driver_age} onChange={(e) => setProfile({ ...profile, driver_age: e.target.value })} className="pb-input" data-testid="mb-driver-age" />
              </Field>
              <Field label="Country">
                <select disabled={locked} value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })} className="pb-input text-sm font-normal" data-testid="mb-country">
                  <option value="">Select country…</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="City">
                <input disabled={locked} value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="pb-input" data-testid="mb-city" />
              </Field>
              <Field label="ZIP / Postcode">
                <input disabled={locked} value={profile.zip} onChange={(e) => setProfile({ ...profile, zip: e.target.value })} className="pb-input" data-testid="mb-zip" />
              </Field>
              <div className="md:col-span-3">
                <Field label="Address">
                  <input disabled={locked} value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} className="pb-input" data-testid="mb-address" />
                </Field>
              </div>
              {/* v4.0.3 — Notes moved INSIDE Driver & Contact card (was
                  previously under Flight, ferry & notes). */}
              <div className="md:col-span-3">
                <Field label="Special requests / Notes">
                  <textarea disabled={locked} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="pb-input" placeholder="Anything we should know? Baby seat needs, arrival time changes, dietary…" data-testid="mb-notes" />
                </Field>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <button disabled={busy || locked} onClick={saveQuick}
                className="bg-black text-white text-base px-6 py-2.5 rounded-lg inline-flex items-center gap-2 hover:bg-neutral-800 disabled:opacity-40 font-medium"
                data-testid="mb-save-profile">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Update Contact Details
              </button>
              {justSaved && (
                <p className="text-xs text-neutral-500" data-testid="mb-post-save-hint">
                  Done managing?{" "}
                  <a href="/" className="underline hover:text-black transition-colors" data-testid="mb-return-home">
                    Return to homepage
                  </a>
                </p>
              )}
            </div>
          </div>

          {/* ── EXTRAS & INSURANCE (v4.0.0) ────────────────────────────── */}
          <ExtrasAddonSection
            booking={booking}
            locked={locked}
            availableExtras={availableExtras}
            selectedExtras={selectedExtras}
            setSelectedExtras={setSelectedExtras}
            onRefresh={onRefresh}
          />

          {/* ── FLIGHT / FERRY (v4.0.3 — Notes moved into Driver card) ── */}
          <div className="bg-white border border-black/10 rounded-xl p-5 sm:p-7 shadow-sm" data-testid="mb-quick-edit-card">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-5">
              <h2 className="font-display text-2xl">Flight &amp; ferry</h2>
              <span className="text-xs uppercase tracking-widest text-neutral-500">Instant save</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-end">
              <Field label="Airline"><input disabled={locked} value={flight.airline_company} onChange={(e) => setFlight({ ...flight, airline_company: e.target.value })} className="pb-input" data-testid="mb-airline" /></Field>
              <Field label="Flight #"><input disabled={locked} value={flight.flight_number} onChange={(e) => setFlight({ ...flight, flight_number: e.target.value })} className="pb-input" data-testid="mb-flight-no" /></Field>
              <Field label="Arrival time"><input disabled={locked} type="time" value={flight.flight_time} onChange={(e) => setFlight({ ...flight, flight_time: e.target.value })} className="pb-input" data-testid="mb-flight-time" /></Field>
              <Field label="Ferry company"><input disabled={locked} value={ferry.company} onChange={(e) => setFerry({ ...ferry, company: e.target.value })} className="pb-input" data-testid="mb-ferry-company" /></Field>
              <Field label="Vessel"><input disabled={locked} value={ferry.vessel_name} onChange={(e) => setFerry({ ...ferry, vessel_name: e.target.value })} className="pb-input" data-testid="mb-ferry-vessel" /></Field>
              <Field label="Ferry arrival"><input disabled={locked} type="time" value={ferry.arrival_time} onChange={(e) => setFerry({ ...ferry, arrival_time: e.target.value })} className="pb-input" data-testid="mb-ferry-arrival" /></Field>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <button disabled={busy || locked} onClick={saveQuick}
                className="bg-black text-white text-base px-6 py-2.5 rounded-lg inline-flex items-center gap-2 hover:bg-neutral-800 disabled:opacity-40 font-medium"
                data-testid="mb-save-quick">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
              </button>
              {justSaved && (
                <p className="text-xs text-neutral-500" data-testid="mb-post-save-hint-quick">
                  Done managing?{" "}
                  <a href="/" className="underline hover:text-black transition-colors">
                    Return to homepage
                  </a>
                </p>
              )}
            </div>
          </div>

          {/* ── RENTAL WINDOW / DATE / VEHICLE — larger typography ────── */}
          <div className="bg-white border border-black/10 rounded-xl p-5 sm:p-7 shadow-sm" data-testid="mb-date-edit-card">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-5">
              <h2 className="font-display text-2xl whitespace-nowrap">Rental window</h2>
              <span className="text-xs uppercase tracking-widest text-amber-700">Requires admin approval</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
              <Field label="Pickup date">
                <EUDatePicker value={dates.pickup_date} onChange={(iso) => setDates((d) => ({ ...d, pickup_date: iso }))} disabled={locked} testId="mb-pickup-date" />
              </Field>
              <Field label="Pickup time">
                <input disabled={locked} type="time" value={dates.pickup_time} onChange={(e) => setDates({ ...dates, pickup_time: e.target.value })} className="pb-input" data-testid="mb-pickup-time" />
              </Field>
              <Field label="Return date">
                <EUDatePicker value={dates.return_date} onChange={(iso) => setDates((d) => ({ ...d, return_date: iso }))} minIso={dates.pickup_date} disabled={locked} testId="mb-return-date" />
              </Field>
              <Field label="Return time">
                <input disabled={locked} type="time" value={dates.return_time} onChange={(e) => setDates({ ...dates, return_time: e.target.value })} className="pb-input" data-testid="mb-return-time" />
              </Field>
            </div>

            {majorDirty && !currentVehicleAvailable && (
              <div className="mt-5 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="mb-vehicle-unavailable-warning">
                <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
                <p><strong>Current vehicle group is fully booked for these dates.</strong> Please pick another available vehicle from the dropdown below.</p>
              </div>
            )}

            {/* v3.9.9 — availability badge (large + prominent) */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 bg-emerald-50/70 border border-emerald-200 rounded-lg px-4 py-3" data-testid="mb-availability-badge">
              <p className="text-sm font-medium text-emerald-900 inline-flex items-center gap-2">
                <Car size={16} />
                {loadingVehicles
                  ? "Checking live availability…"
                  : (
                    <span>
                      <strong className="text-lg tabular-nums">{availableVehicles.length}</strong>{" "}
                      vehicle{availableVehicles.length === 1 ? "" : "s"} available for these dates
                    </span>
                  )}
              </p>
              <span className="text-xs uppercase tracking-widest text-emerald-700">Live inventory</span>
            </div>

            <div className="mt-4">
              <Field label="Change vehicle group (optional)">
                <select
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className="pb-input"
                  disabled={locked}
                  data-testid="mb-vehicle-select"
                >
                  <option value={booking.vehicle_id || ""}>
                    {booking.vehicle_snapshot?.name || "Current vehicle"} (current)
                  </option>
                  {availableVehicles
                    .filter((v) => v.id !== booking.vehicle_id)
                    .map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.brand ? `${v.brand} ${v.name}` : v.name} — €{Number(v.daily_rate || 0).toFixed(0)}/day
                      </option>
                    ))}
                </select>
              </Field>
            </div>

            <button disabled={busy || locked || !majorDirty} onClick={requestChange}
              className="mt-5 bg-amber-500 text-white text-base px-6 py-2.5 rounded-lg inline-flex items-center gap-2 hover:bg-amber-600 disabled:opacity-40 font-medium"
              data-testid="mb-request-modification">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Clock size={16} />} Submit modification request
            </button>
            <p className="text-sm text-neutral-600 mt-2">Our team reviews modification requests within business hours.</p>
          </div>
        </div>

        {/* ── Sidebar — Reservations Team (v3.9.9 · v4.0.1 dynamic) ─── */}
        <aside className="lg:sticky lg:top-24 h-fit" data-testid="mb-reservations-team">
          {(() => {
            // v4.0.1 — Pull phone / email dynamically from public business
            // settings; fall back to the previous static values only when
            // settings are still loading or completely empty.
            const teamPhone = publicSettings?.support_phone || "+30 22870 12345";
            const teamEmail = publicSettings?.support_email || "reservations@brainart.gr";
            return (
              <div className="bg-neutral-900 text-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <HeadphonesIcon size={18} className="text-emerald-300" />
                  <p className="text-xs uppercase tracking-[0.25em] text-neutral-300">Reservations Team</p>
                </div>
                <p className="font-display text-xl leading-tight text-white">
                  Need immediate assistance?
                </p>
                <p className="text-sm text-neutral-300 mt-2 leading-relaxed">
                  Our office team is on hand every day from 8am to 8pm. Reach out for anything the portal can't handle — same-day changes, group bookings, VIP requests.
                </p>
                <div className="mt-5 space-y-2.5">
                  <a href={`tel:${teamPhone.replace(/\s+/g, "")}`} className="flex items-center gap-2 text-base font-medium text-white hover:text-emerald-300 transition-colors" data-testid="mb-team-phone">
                    <Phone size={15} /> {teamPhone}
                  </a>
                  <a href={`mailto:${teamEmail}`} className="flex items-center gap-2 text-sm text-neutral-100 hover:text-emerald-300 transition-colors break-all" data-testid="mb-team-email">
                    <Mail size={14} /> {teamEmail}
                  </a>
                </div>
                <div className="mt-5 pt-5 border-t border-white/10 flex items-center gap-2 text-xs text-neutral-400">
                  <ShieldCheck size={12} className="text-emerald-300" /> Secure customer portal
                </div>
              </div>
            );
          })()}
        </aside>
      </div>
    </section>
  );
}

// v3.9.8 — Human-readable pending modification card. Replaces the raw
// JSON dump with a labelled diff plus the recalculated price impact.
function PendingModificationCard({ pending, bookingTotal }) {
  const req = pending.requested || {};
  const prev = pending.previous || {};
  const newTotal = pending.new_total != null ? Number(pending.new_total) : null;
  const priceDiff = pending.price_diff != null ? Number(pending.price_diff) : null;

  const lines = [];
  if ("pickup_date" in req || "pickup_time" in req) {
    lines.push({
      label: "Pickup",
      value: `${formatEU(req.pickup_date || prev.pickup_date)} ${req.pickup_time || prev.pickup_time}`,
    });
  }
  if ("return_date" in req || "return_time" in req) {
    lines.push({
      label: "Return",
      value: `${formatEU(req.return_date || prev.return_date)} ${req.return_time || prev.return_time}`,
    });
  }
  if ("pickup_location" in req) lines.push({ label: "Pickup location", value: req.pickup_location });
  if ("return_location" in req) lines.push({ label: "Return location", value: req.return_location });
  if ("vehicle_id" in req && pending.swap_vehicle) {
    lines.push({
      label: "Vehicle",
      value: `${pending.swap_vehicle.brand ? pending.swap_vehicle.brand + " " : ""}${pending.swap_vehicle.name}`,
    });
  }
  if ("addons" in req) {
    lines.push({
      label: "Add-ons",
      value: (req.addons || []).join(", ") || "None",
    });
  }

  // Compute day delta so we can render "(+1 day)" cues.
  let dayDelta = null;
  if (req.pickup_date || req.return_date) {
    try {
      const oldStart = new Date(prev.pickup_date);
      const oldEnd   = new Date(prev.return_date);
      const newStart = new Date(req.pickup_date || prev.pickup_date);
      const newEnd   = new Date(req.return_date || prev.return_date);
      const oldDays  = Math.max(1, Math.round((oldEnd - oldStart) / 86_400_000));
      const newDays  = Math.max(1, Math.round((newEnd - newStart) / 86_400_000));
      dayDelta = newDays - oldDays;
    } catch { /* ignore */ }
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-base text-amber-900 shadow-sm" data-testid="mb-pending-notice">
      <p className="font-semibold text-lg">Modification pending admin approval.</p>
      <p className="text-sm text-amber-800 mt-1">We'll email you as soon as the office reviews it.</p>

      <ul className="mt-4 space-y-1.5 text-base" data-testid="mb-pending-summary">
        {lines.map((l, i) => (
          <li key={i}>
            <span className="text-xs uppercase tracking-widest text-amber-700 mr-2">{l.label}:</span>
            <span className="font-semibold text-neutral-900">{l.value}</span>
            {i === 1 && dayDelta !== null && dayDelta !== 0 && (
              <span className="ml-2 text-sm text-amber-700">({dayDelta > 0 ? "+" : ""}{dayDelta} day{Math.abs(dayDelta) === 1 ? "" : "s"})</span>
            )}
          </li>
        ))}
      </ul>

      {(priceDiff !== null || newTotal !== null) && (
        <div className="mt-5 pt-4 border-t border-amber-300 flex flex-wrap items-baseline gap-x-8 gap-y-2" data-testid="mb-pending-price">
          {priceDiff !== null && (
            <p>
              <span className="text-xs uppercase tracking-widest text-amber-700 mr-2">Price difference:</span>
              <strong className={`text-xl ${priceDiff > 0 ? "text-rose-700" : priceDiff < 0 ? "text-emerald-700" : ""}`}>
                {priceDiff > 0 ? "+" : ""}{fmtEUR(priceDiff)}
              </strong>
            </p>
          )}
          {newTotal !== null && (
            <p>
              <span className="text-xs uppercase tracking-widest text-amber-700 mr-2">New total:</span>
              <strong className="text-xl text-neutral-900">{fmtEUR(newTotal)}</strong>
              {bookingTotal != null && (
                <span className="ml-2 text-sm text-amber-700 line-through">was {fmtEUR(bookingTotal)}</span>
              )}
            </p>
          )}
        </div>
      )}

      {pending.reason && (
        <p className="text-sm italic mt-4 text-amber-800">"{pending.reason}"</p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block min-w-0">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5 truncate">{label}</span>
      {children}
    </label>
  );
}

// v4.0.0 — Extras & Insurance self-service. Fetches active extras
// catalogue, pre-checks addons already in the booking, and submits the
// diff as a modification_requested so ops can approve the price impact.
function ExtrasAddonSection({ booking, locked, availableExtras, selectedExtras, setSelectedExtras, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const days = Math.max(1, Number(booking.days || 1));
  const originalSet = useMemo(() => new Set(booking.addons || []), [booking.addons]);
  const currentSet = useMemo(
    () => new Set(Object.entries(selectedExtras).filter(([, v]) => v).map(([k]) => k)),
    [selectedExtras],
  );
  const dirty = useMemo(() => {
    if (originalSet.size !== currentSet.size) return true;
    for (const id of currentSet) if (!originalSet.has(id)) return true;
    return false;
  }, [originalSet, currentSet]);

  // Live-preview of the price delta (client-side estimate; the server
  // still re-quotes authoritatively when the modification is submitted).
  const priceDelta = useMemo(() => {
    let delta = 0;
    for (const e of availableExtras) {
      const wasOn = originalSet.has(e.id);
      const isOn  = currentSet.has(e.id);
      if (wasOn === isOn) continue;
      const line = e.pricing_type === "per_day"
        ? Number(e.price || 0) * days
        : Number(e.price || 0);
      delta += isOn ? line : -line;
    }
    return Math.round(delta * 100) / 100;
  }, [availableExtras, currentSet, originalSet, days]);

  const submit = async () => {
    if (locked) { toast.error("Modifications are locked within the pickup cutoff window."); return; }
    if (!dirty) { toast.warning("No add-ons changed."); return; }
    setBusy(true);
    try {
      await api.post(`/public/manage-booking/${booking.id}/request-modification`, {
        email:  booking.user_email,
        addons: Array.from(currentSet),
        reason: "Customer updated extras from the self-service portal.",
      });
      toast.success("Add-on change submitted — the office will confirm shortly.");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-black/10 rounded-xl p-5 sm:p-7 shadow-sm" data-testid="mb-extras-card">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h2 className="font-display text-2xl inline-flex items-center gap-2">
          <Package size={18} /> Extras &amp; Insurance
        </h2>
        <span className="text-xs uppercase tracking-widest text-amber-700">Requires admin approval</span>
      </div>
      <p className="text-sm text-neutral-500 mb-4">
        Tick or untick add-ons for your reservation. Any price change is calculated live below and confirmed by the office after review.
      </p>

      {availableExtras.length === 0 && (
        <p className="text-sm text-neutral-500 italic" data-testid="mb-extras-empty">
          No extras catalogue available.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mb-extras-list">
        {availableExtras.map((e) => {
          const isOn = !!selectedExtras[e.id];
          const wasOn = originalSet.has(e.id);
          const unit = e.pricing_type === "per_day" ? "/day" : "flat";
          const lineTotal = e.pricing_type === "per_day"
            ? Number(e.price || 0) * days
            : Number(e.price || 0);
          return (
            <label
              key={e.id}
              className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                isOn
                  ? "border-black bg-neutral-50"
                  : "border-black/10 hover:border-black/40 bg-white"
              } ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid={`mb-extra-${e.id}`}
            >
              <span className="flex items-center gap-3 text-sm min-w-0 flex-1">
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={isOn}
                  onChange={() => setSelectedExtras((s) => ({ ...s, [e.id]: !s[e.id] }))}
                  className="w-4 h-4"
                  data-testid={`mb-extra-cbx-${e.id}`}
                />
                <span>
                  <span className="font-medium text-neutral-900">{e.name}</span>
                  {wasOn && <span className="ml-2 text-[10px] uppercase tracking-widest text-neutral-500">Included</span>}
                </span>
              </span>
              <span className="text-sm tabular-nums text-neutral-700 flex flex-wrap items-baseline gap-x-1.5 shrink-0">
                <span>+{fmtEUR(e.price)}<span className="text-neutral-400"> {unit}</span></span>
                {e.pricing_type === "per_day" && (
                  <span className="text-xs text-neutral-500">· {fmtEUR(lineTotal)} total</span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {dirty && (
        <div className="mt-5 flex flex-wrap items-baseline justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="mb-extras-delta">
          <p className="text-sm text-amber-900">
            <span className="text-xs uppercase tracking-widest text-amber-700 mr-2">Estimated price impact:</span>
            <strong className={`text-lg ${priceDelta > 0 ? "text-rose-700" : priceDelta < 0 ? "text-emerald-700" : ""}`}>
              {priceDelta > 0 ? "+" : ""}{fmtEUR(priceDelta)}
            </strong>
          </p>
          <p className="text-xs text-amber-800">The office confirms the final total after review.</p>
        </div>
      )}

      <button
        type="button"
        disabled={busy || locked || !dirty}
        onClick={submit}
        className="mt-5 bg-amber-500 text-white text-base px-6 py-2.5 rounded-lg inline-flex items-center gap-2 hover:bg-amber-600 disabled:opacity-40 font-medium"
        data-testid="mb-save-extras"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />} Update extras
      </button>
    </div>
  );
}
